import type {
  AnyObject,
  ArraySchema,
  StringSchema,
} from 'yup';
import * as yup from 'yup';

import { DRAW_LAYER_NAME_MAX_LENGTH } from '@nora-monorepo/shared';

type Translate = (key: string, params?: unknown) => string;

interface DrawLayerListItem {
  name: string;
  isDefault: boolean;
}

/**
 * Creates the yup schema for draw layer names.
 *
 * This validates the final draw layer name that will be submitted, including
 * generated default names.
 */
export function createDrawLayerNameSchema(t: Translate): StringSchema<string | undefined> {
  return yup
    .string()
    .trim()
    .required(t('operation.validation.drawLayerNameRequired'))
    .max(
      DRAW_LAYER_NAME_MAX_LENGTH,
      t('operation.validation.drawLayerNameMaxLengthExceeded', [DRAW_LAYER_NAME_MAX_LENGTH]),
    );
}

/**
 * Creates the yup schema for operation draw layers.
 *
 * This validates each draw layer name and ensures an operation always has a
 * selected default draw layer before submission.
 *
 * @param t
 * @param requireDefaultDrawLayer - default is to require default draw layers, but old operations and operations created on mobile have no default draw layer, so it is possible to bypass this requirement in those cases.
 *
 */
export function createDrawLayerListSchema(
  t: Translate,
  requireDefaultDrawLayer: boolean = true,
): ArraySchema<DrawLayerListItem[], AnyObject, never[], 'd'> {
  const defaultDrawLayerRequiredMessage = t('operation.validation.defaultDrawLayerRequired');

  return yup
    .array()
    .of(yup.object({
      name: createDrawLayerNameSchema(t).default(''),
      isDefault: yup.boolean().required().default(false),
    }))
    .required()
    .default([])
    .test(
      'has-default-draw-layer',
      defaultDrawLayerRequiredMessage,
      (drawLayers) => {
        return !requireDefaultDrawLayer || (drawLayers?.some(drawLayer => drawLayer.isDefault) ?? false);
      },
    );
}
